<template>
  <v-container fluid id="committed-timber">
    <v-row>
      <v-col cols="auto" v-if="totalsVisible">
        <CommittedTimberTotalsCard :totals="committedTimberTotals"/>
      </v-col>

      <v-col class="fill-width" style="overflow: auto; min-width: 360px">
        <v-row dense>
          <v-col>
            <v-row align="center">
              <v-col cols="auto">
                <v-tooltip color="primary" bottom>
                  <template #activator="tooltip">
                    <v-icon @click="toggleTotalsVisible" id="committed-timber-totals-button" class="mr-n1" color="primary"
                      v-on="{ ...tooltip.on }">
                      mdi-poll
                    </v-icon>
                  </template>
                  <span class="white--text subtitle-1">{{ $t(totalsVisible ? 'hide' : 'show').concat(' ', $t('totals')) }}</span>
                </v-tooltip>
              </v-col>
              <v-col>
                <v-text-field
                  style="min-width: 128px;"
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="$t('search')"
                  single-line
                  clearable
                  color="primary"
                />
              </v-col>
              <v-col cols="auto">
                <v-row dense>
                  <v-col cols="auto">
                    <Icon
                      elementId="tract-supplier-toggle"
                      :icon="groupBySupplier ? 'mdi-crosshairs-gps' : 'mdi-account'"
                      :small="false"
                      :tooltipText="groupBySupplier ? $t('showAllTracts') : $t('groupBySupplier')"
                      @icon-clicked="switchViewMode"
                      margin="mr-1"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <Icon
                      @icon-clicked="myTractsClicked"
                      :small="false"
                      :icon="IconHelpers.getIconForObjectType('myTracts', committedTimberFilter.myTracts)"
                      :tooltipText="committedTimberFilter.myTracts ? $t('allTracts') : $t('myTracts')"
                      elementId="my-tracts-committed-timber"
                      margin=""
                    />
                  </v-col>
                  <v-col cols="auto" class="mr-n1">
                    <CommittedTimberFilter
                      @input="applyFilters"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <ColumnAdjuster
                      :propColumnSet.sync="columnSet"
                      @new-columns-selected="newColumnsSelected"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <Icon
                      icon="mdi-file-delimited-outline"
                      :tooltipText="$t('downloadCSV')"
                      @icon-clicked="downloadCSV"
                      :small="false"
                      margin=""
                    />
                  </v-col>
                  <v-col cols="auto">
                    <Icon
                      icon="mdi-refresh"
                      :tooltipText="$t('refresh')"
                      @icon-clicked="refreshTracts"
                      :small="false"
                      margin=""
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <v-chip v-for="fk in activeFilters" :key="fk.key" color="secondary" class="white--text mr-1 mb-1" close @click:close="removeFilterField(fk.key)">
              <span style="font-weight: bold;">{{$t(fk.label)}}</span>
              <span v-if="!!fk.text">{{`: ${fk.text}`}}</span>
            </v-chip>
          </v-col>
          <v-col cols="12" lg="" class="fill-width" style="min-width: 0;" v-if="groupBySupplier">
            <SupplierCommittedTimberPanels
              :committedTimberBySupplier="committedTimberBySupplier"
              :customCells="customCells"
              :headers="headers"
              :tractsLoading="tractsLoading"
              :search="search"
              @remove-filter="removeFilterField"
              @open-detail="openDetailDialog"
            />
          </v-col>
          <v-col cols="12" lg="" class="fill-width" style="min-width: 0;" v-else>
            <DataTable
            :items="committedTimber"
            :customCells="customCells"
            :headers="headers"
            :loading="tractsLoading"
            :enableExternalSearch="true"
            :externalSearch="search"
            :showSearch="false"
            resize
            :tableHeightCalculationData="{ offset: 473 }"
            containerClass="px-0"
            >
              <template #actions="{item}">
                <Icon
                icon="mdi-information-outline"
                :small="false"
                :tooltipText="$t('viewDetailedBreakdown')"
                @icon-clicked="openDetailDialog(item)"/>
              </template>
              <template #status="{item}">
                <TractStatusIcon :status="item.status"/>
                <Icon
                v-if="dateIsUnderCountUnitsAway(item.harvestEndDate, 1, 'week')"
                icon="mdi-alert-outline"
                iconColor="warning"
                :tooltipText="$t('tractHarvestWarning1w')"
                />
                <Icon
                v-if="item.paused"
                icon="mdi-pause-circle"
                iconColor="warning"
                :tooltipText="$t('tractPaused')"
                />
              </template>
              <template #team="{item}">
                <v-row no-gutters>
                  <v-col cols="auto">
                    <span>{{ item.teamName ?? $t('notAvailable') }}</span>
                  </v-col>
                  <v-col cols="auto">
                    <Icon
                    v-if="!!item.teamName"
                    iconColor="black"
                    icon="mdi-dots-horizontal"
                    margin="ml-1"
                    :tooltipText="getTeamTooltip(item.tractUserNames)"
                    />
                  </v-col>
                </v-row>
              </template>
            </DataTable>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <Dialog :stateId="dialogId" @dialog-closing="resetDialogs">
      <TractDetail
      v-if="focusedTractId"
      :tractId="focusedTractId"
      @edit-tract="editTract"
      @view-tract="focusedTractId = $event.tractId"
      :tabToOpen="1"/>
    </Dialog>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { localeCompareAlphanumeric } from '@/utils/base'
import RouterJump from '@/model/RouterJump.js'
import tractHeaders from '@/headers/Tract.js'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations.js'
import { TractStatus } from '@/utils/Enumerations'
import { generateCsvString } from '@/utils/CSVUtility'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { dateIsUnderCountUnitsAway } from '@/utils/DateFormatter'
import IconHelpers from '@/utils/IconHelpers'
import { groupBy } from 'lodash'

export default {
  name: 'CommittedTimber',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    TractDetail: () => import('@/components/tract/tract-detail/TractDetail.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    CommittedTimberFilter: () => import('./CommittedTimberFilter.vue'),
    ColumnAdjuster: () => import('@/components/core/ColumnAdjuster.vue'),
    TractStatusIcon: () => import('@/components/tract/TractStatusIcon.vue'),
    SupplierCommittedTimberPanels: () => import('@/components/tract/tract-analysis/SupplierCommittedTimberPanels'),
    CommittedTimberTotalsCard: () => import('@/components/tract/tract-analysis/CommittedTimberTotalsCard.vue')
  },

  data: () => ({
    IconHelpers,
    dialogId: uniqueDialogId('committed-timber'),
    applyButtonDisabled: true,
    focusedTractId: null,
    committedTimber: [],
    committedTimberBySupplier: [],
    filters: {
      status: [],
      tractForester: [],
      tractType: [],
      team: [],
      myTracts: false
    },
    totals: {
      expected: 0,
      harvested: 0,
      expectedMBF: 0,
      harvestedMBF: 0,
      overcut: 0,
      cruisedValue: 0,
      tractCost: 0,
      stumpageValue: 0,
      cutoutValue: 0
    },
    columnSet: null,
    groupBySupplier: false,
    search: '',
    totalsVisible: localStorage.getItem(LocalStorageKeys.COMMITTED_TIMBER_TOTALS_VISIBLE) === 'true'
  }),

  computed: {
    ...mapGetters('tract', ['tractsLoading', 'committedTimberFilter']),
    ...mapGetters('tract-type', ['allTractTypes']),
    headers () {
      return this.columnSet?.getColumns() ?? []
    },

    customCells () {
      return [
        {
          slotName: 'actions',
          value: 'actions'
        },
        {
          slotName: 'team',
          value: 'teamName'
        },
        {
          slotName: 'status',
          value: 'status'
        }
      ]
    },

    activeFilters () {
      const keys = Object.keys(this.committedTimberFilter)
      return keys.map(key => ({
        key,
        label: key === 'tractForester' ? 'forester' : key,
        filter: this.committedTimberFilter[key],
        text: this.getFilterChipText(key, this.committedTimberFilter[key])
      }))
        .filter(kf => {
          switch (kf.label) {
            case 'myTracts':
              return kf.filter === true
            case 'forester':
            case 'tractType':
            case 'team':
            case 'status':
            default:
              return kf.filter.length > 0
          }
        })
    },

    committedTimberTotals () {
      const totals = (this.committedTimber ?? []).reduce((total, tract) => {
        total.expectedTons += tract.expectedTons
        total.harvestedTons += tract.harvestedTons
        total.remainingTons += tract.remainingTons
        total.expectedMBF += tract.expectedMBF
        total.harvestedMBF += tract.harvestedMBF
        total.remainingMBF += tract.remainingMBF
        total.cruisedValue += tract.cruisedValue
        return total
      }, {
        expectedTons: 0,
        harvestedTons: 0,
        remainingTons: 0,
        expectedMBF: 0,
        harvestedMBF: 0,
        remainingMBF: 0,
        cruisedValue: 0
      })

      if (totals.expectedMBF > 0) {
        return [
          { label: this.$t('expectedTons'), value: numberWithCommas(totals.expectedTons, 3) },
          { label: this.$t('harvestedTons'), value: numberWithCommas(totals.harvestedTons, 3) },
          { label: this.$t('remainingTons'), value: numberWithCommas(totals.remainingTons, 3) },
          { label: this.$t('expectedMBF'), value: numberWithCommas(totals.expectedMBF, 3) },
          { label: this.$t('harvestedMBF'), value: numberWithCommas(totals.harvestedMBF, 3) },
          { label: this.$t('remainingMBF'), value: numberWithCommas(totals.remainingMBF, 3) },
          { label: this.$t('cruisedValue'), value: formatMoney(totals.cruisedValue) }
        ]
      }
      return [
        { label: this.$t('expectedTons'), value: numberWithCommas(totals.expectedTons, 3) },
        { label: this.$t('harvestedTons'), value: numberWithCommas(totals.harvestedTons, 3) },
        { label: this.$t('remainingTons'), value: numberWithCommas(totals.remainingTons, 3) },
        { label: this.$t('cruisedValue'), value: formatMoney(totals.cruisedValue) }
      ]
    }
  },

  watch: {
    committedTimberFilter: {
      handler () {
        this.refreshTracts()
      },
      deep: true
    }
  },

  async created () {
    this.initializeHeaders()
    this.groupBySupplier = localStorage.getItem(LocalStorageKeys.COMMITTED_TIMBER_GROUP_BY_SUPPLIER) === 'true'
  },

  methods: {
    ...mapActions('tract', ['fetchCommittedTimber', 'setCommittedTimberFilter']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapMutations('global', ['setRouterJump']),
    formatMoney,
    numberWithCommas,
    generateCsvString,
    dateIsUnderCountUnitsAway,

    initializeHeaders () {
      this.columnSet = tractHeaders.committedTimberHeaders()
    },

    newColumnsSelected (columnSet) {
      this.columnSet = columnSet
    },

    async refreshTracts () {
      const timberData = await this.fetchCommittedTimber({
        statuses: this.committedTimberFilter.status,
        tractTypes: this.committedTimberFilter.tractType,
        tractForesters: this.committedTimberFilter.tractForester,
        teams: this.committedTimberFilter.team,
        myTracts: this.committedTimberFilter.myTracts
      })
      this.committedTimber = timberData
      const committedTimberGrouped = groupBy(timberData, 'supplier')
      this.committedTimberBySupplier = Object.keys(committedTimberGrouped).map(td => {
        return {
          name: td !== '' ? td : this.$t('noAssignedSupplier'),
          tracts: committedTimberGrouped[td]
        }
      }).sort((supplierOne, supplierTwo) => this.sortSuppliers(supplierOne, supplierTwo))
      this.applyButtonDisabled = true
    },

    switchViewMode () {
      this.groupBySupplier = !this.groupBySupplier
      const localStorageSetting = this.groupBySupplier ? 'true' : 'false'
      localStorage.setItem(LocalStorageKeys.COMMITTED_TIMBER_GROUP_BY_SUPPLIER, localStorageSetting)
    },

    ignoreFilter (filter) {
      return (filter.tractType.length === 0 && filter.status.length === 0)
    },

    applyFilters (filters) {
      this.filters = filters
    },

    getFilterChipText (key, filter) {
      switch (key) {
        case 'status':
          return filter.map(f => TractStatus.fromInt(f)).join(', ')
        case 'tractType':
        case 'tractForester':
        case 'team':
          return filter.map(val => val.name).join(', ')
        case 'myTracts':
        default:
          return ''
      }
    },

    removeFilterField (fieldKey) {
      let removedValue = []
      switch (fieldKey) {
        case 'myTracts':
          removedValue = false
          break
        case 'status':
        case 'tractType':
        case 'tractForester':
        case 'team':
        default:
          removedValue = []
          break
      }

      const f = {
        ...this.committedTimberFilter,
        [fieldKey]: removedValue
      }

      this.setCommittedTimberFilter(f)
    },

    downloadCSV () {
      const csvString = this.generateCsvString(this.committedTimber, [
        { label: 'tract', value: ct => ct.tractName },
        { label: 'status', value: ct => TractStatus.fromInt(ct.status) },
        { label: 'type', value: ct => ct.tractType },
        { label: 'team', value: ct => ct.teamName },
        { label: 'supplier', value: ct => ct.supplier },
        { label: 'logger', value: ct => ct.tractLogger },
        { label: 'forester', value: ct => ct.tractForester },
        { label: 'loggingAccessibility', value: ct => ct.tractLoggingAccessibility },
        { label: 'cruisedValue', value: ct => ct.cruisedValue },
        { label: 'harvestedTons', value: ct => ct.harvestedTons },
        { label: 'expectedTons', value: ct => ct.expectedTons },
        { label: 'remainingTons', value: ct => ct.remainingTons },
        { label: 'harvestedMBF', value: ct => ct.harvestedMBF },
        { label: 'expectedMBF', value: ct => ct.expectedMBF },
        { label: 'remainingMBF', value: ct => ct.remainingMBF },
        { label: 'rpi', value: ct => ct.rpi },
        { label: 'dbh', value: ct => ct.dbh },
        { label: 'age', value: ct => ct.age }

      ])
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      saveAs(blob, `Committed-Timber-${moment().format()}.csv`)
    },

    openDetailDialog (tract) {
      this.focusedTractId = tract.tractId
      this.openOrUpdateDialog({ id: this.dialogId, width: '90vw' })
    },

    editTract () {
      const routerJump = new RouterJump('CommittedTimber', 'Tracts', {
        tractId: this.focusedTractId
      })
      this.setRouterJump(routerJump)
      this.$router.push('tracts')
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.focusedTractId = null
    },

    myTractsClicked () {
      this.setCommittedTimberFilter({
        ...this.committedTimberFilter,
        myTracts: !this.committedTimberFilter.myTracts
      })
    },

    getTeamTooltip (tractUsers) {
      return tractUsers
        .toString()
        .replaceAll(',', '\n')
    },

    sortSuppliers (supplierOne, supplierTwo) {
      if (supplierOne.name === this.$t('noAssignedSupplier')) return 1
      else if (supplierTwo.name === this.$t('noAssignedSupplier')) return -1
      else return localeCompareAlphanumeric(supplierOne.name, supplierTwo.name)
    },

    toggleTotalsVisible () {
      this.totalsVisible = !this.totalsVisible
      localStorage.setItem(LocalStorageKeys.COMMITTED_TIMBER_TOTALS_VISIBLE, this.totalsVisible ? 'true' : 'false')
    }
  }
}
</script>
